import type { SupportSessionConfig } from '@opendoor/auth-fe/support-session';
import { getSupportSessionRedirectUrl, selectedCustomer } from '@opendoor/auth-fe/support-session';
import { z } from 'zod';

import { DEPLOY_ENV } from '../globals';

type CosmosSupportConfig = Readonly<{
  applicationOrigin: string;
  authOrigin: string | null;
  entryOrigins: readonly string[];
  publicOrigins: readonly string[];
  consumerOrigin: string;
  consumerSupportOrigin: string;
}>;

const PRODUCTION_CONFIG: CosmosSupportConfig = {
  applicationOrigin: 'https://cosmos.apps.opendoor.com',
  authOrigin: 'https://auth.apps.opendoor.com',
  entryOrigins: [
    'https://www.opendoor.com',
    'https://buy.opendoor.com',
    'https://cf-cosmos.opendoor.com',
  ],
  publicOrigins: [
    'https://www.opendoor.com',
    'https://buy.opendoor.com',
    'https://cf-cosmos.opendoor.com',
  ],
  consumerOrigin: 'https://consumer.opendoor.com',
  consumerSupportOrigin: 'https://consumer.apps.opendoor.com',
};

const STAGING_CONFIG: CosmosSupportConfig = {
  applicationOrigin: 'https://cosmos.apps-staging.opendoor.com',
  authOrigin: 'https://auth.apps-staging.opendoor.com',
  entryOrigins: [
    'https://demo.simplersell.com',
    'https://qa.simplersell.com',
    'https://buy.simplersell.com',
    'https://staging-buy.simplersell.com',
  ],
  publicOrigins: [
    'https://demo.simplersell.com',
    'https://qa.simplersell.com',
    'https://buy.simplersell.com',
    'https://staging-buy.simplersell.com',
  ],
  consumerOrigin: 'https://consumer.simplersell.com',
  consumerSupportOrigin: 'https://consumer.apps-staging.opendoor.com',
};

const customerUUIDSchema = z.uuid().transform((value) => value.toLowerCase());

const localdevConfig = (url: URL): CosmosSupportConfig | null => {
  if (!/^(www|buy|cf-cosmos)\.\d+\.localhost$/.test(url.hostname)) return null;

  const originFor = (prefix: string) => {
    const origin = new URL(url.origin);
    origin.hostname = url.hostname.replace(/^[^.]+/, prefix);
    return origin.origin;
  };
  const publicOrigin = originFor('www');
  const buyOrigin = originFor('buy');
  const consumerOrigin = originFor('consumer');
  return {
    applicationOrigin: originFor('cf-cosmos'),
    authOrigin: null,
    entryOrigins: [publicOrigin, buyOrigin],
    publicOrigins: [publicOrigin, buyOrigin],
    consumerOrigin,
    consumerSupportOrigin: consumerOrigin,
  };
};

const supportConfig = (
  currentUrl: string,
  environment: typeof DEPLOY_ENV,
): CosmosSupportConfig | null => {
  if (environment === 'production') return PRODUCTION_CONFIG;
  if (environment === 'staging' || environment === 'qa') return STAGING_CONFIG;
  if (environment === 'localdev') return localdevConfig(new URL(currentUrl));
  return null;
};

export const hasLegacyCustomerToken = (search: string): boolean =>
  new URLSearchParams(search).has('human_token');

export const urlWithoutSupportSelection = (
  currentUrl: string,
  environment: typeof DEPLOY_ENV = DEPLOY_ENV,
): string => {
  const url = new URL(currentUrl);
  const config = supportConfig(currentUrl, environment);
  if (config && url.origin === config.applicationOrigin) {
    const publicOrigin = new URL(config.publicOrigins[0]);
    url.protocol = publicOrigin.protocol;
    url.host = publicOrigin.host;
  }
  url.searchParams.delete('customer');
  url.searchParams.delete('human_token');
  return url.toString();
};

const urlWithCustomer = (currentUrl: string, customerUUID: string): string => {
  const url = new URL(currentUrl);
  url.searchParams.set('customer', customerUUID);
  url.searchParams.delete('human_token');
  return url.toString();
};

export const isSupportApplicationUrl = (
  currentUrl: string,
  environment: typeof DEPLOY_ENV = DEPLOY_ENV,
): boolean =>
  supportConfig(currentUrl, environment)?.applicationOrigin === new URL(currentUrl).origin;

export const supportSignInUrl = (
  currentUrl: string,
  environment: typeof DEPLOY_ENV = DEPLOY_ENV,
): string | null => {
  const current = new URL(currentUrl);
  const customerUUID = selectedCustomer(current.search).customerUUID;
  const config = supportConfig(currentUrl, environment);
  if (!config || !customerUUID) return null;
  if (
    current.origin !== config.applicationOrigin &&
    !config.entryOrigins.includes(current.origin)
  ) {
    return null;
  }
  if (current.origin === config.applicationOrigin) {
    return hasLegacyCustomerToken(current.search)
      ? urlWithCustomer(currentUrl, customerUUID)
      : null;
  }

  const selectedUrl = new URL(urlWithCustomer(currentUrl, customerUUID));
  selectedUrl.protocol = new URL(config.applicationOrigin).protocol;
  selectedUrl.host = new URL(config.applicationOrigin).host;
  if (config.authOrigin === null) return selectedUrl.toString();

  const sharedConfig: SupportSessionConfig = {
    applicationOrigin: config.applicationOrigin,
    authOrigin: config.authOrigin,
    entryOrigins: config.entryOrigins,
  };
  return getSupportSessionRedirectUrl({
    currentUrl: urlWithCustomer(currentUrl, customerUUID),
    config: sharedConfig,
    shouldAuthenticate: true,
  });
};

export const supportReauthenticationUrl = ({
  currentUrl,
  customerUUID,
  environment = DEPLOY_ENV,
}: {
  currentUrl: string;
  customerUUID: string;
  environment?: typeof DEPLOY_ENV;
}): string | null => {
  const parsedCustomerUUID = customerUUIDSchema.safeParse(customerUUID);
  const config = supportConfig(currentUrl, environment);
  if (
    !parsedCustomerUUID.success ||
    !config?.authOrigin ||
    new URL(currentUrl).origin !== config.applicationOrigin
  ) {
    return null;
  }

  return getSupportSessionRedirectUrl({
    currentUrl: urlWithCustomer(currentUrl, parsedCustomerUUID.data),
    config: {
      applicationOrigin: config.applicationOrigin,
      authOrigin: config.authOrigin,
      entryOrigins: config.entryOrigins,
    },
    shouldAuthenticate: true,
  });
};

const COSMOS_PRODUCT_PATH =
  /^\/(account|buy-direct-checkout|checkout|dashboard|error|favorites|feed|homes|hub|make-offer|manual-address-entry|marketplace|nl-search|properties|self-tour)(\/|$)/;

const SUPPORT_UNAVAILABLE_PATH = /^\/self-tour(\/|$)/;

export const isSupportRouteUnavailable = (pathname: string): boolean =>
  SUPPORT_UNAVAILABLE_PATH.test(pathname);

export const supportNavigationHref = ({
  href,
  currentUrl,
  customerUUID,
  environment = DEPLOY_ENV,
}: {
  href: string;
  currentUrl: string;
  customerUUID: string;
  environment?: typeof DEPLOY_ENV;
}): string => {
  const current = new URL(currentUrl);
  const config = supportConfig(currentUrl, environment);
  if (!config || current.origin !== config.applicationOrigin) return href;

  let destination: URL;
  try {
    destination = new URL(href, current);
  } catch {
    return href;
  }

  if (
    destination.origin === config.consumerOrigin ||
    destination.origin === config.consumerSupportOrigin
  ) {
    const consumerSupport = new URL(config.consumerSupportOrigin);
    destination.protocol = consumerSupport.protocol;
    destination.host = consumerSupport.host;
  } else if (
    config.publicOrigins.includes(destination.origin) &&
    COSMOS_PRODUCT_PATH.test(destination.pathname)
  ) {
    destination.protocol = current.protocol;
    destination.host = current.host;
  } else if (destination.origin !== current.origin) {
    return href;
  }

  destination.searchParams.set('customer', customerUUID);
  destination.searchParams.delete('human_token');
  return destination.origin === current.origin
    ? `${destination.pathname}${destination.search}${destination.hash}`
    : destination.toString();
};

export const exitSupportSession = (): void => {
  window.location.replace(urlWithoutSupportSelection(window.location.href));
};
